import { Accordion, AccordionDetails, Box, Button, Paper, Typography } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ProductCards } from "../../SalesProductsPage/ProductConfigurationDialog/ProductCards";
import { ProductDocuments } from "../../SalesProductsPage/ProductConfigurationDialog/ProductDocuments";
import { ProductNoteControl } from "../../SalesProductsPage/ProductConfigurationDialog/ProductNoteControl";
import { ProductHotelNoteControl } from "../../SalesProductsPage/ProductConfigurationDialog/ProductHotelNoteControl";
import { ProductParameters } from "../../SalesProductsPage/ProductConfigurationDialog/ProductParameters";
import { ProductPricing } from "../../SalesProductsPage/ProductConfigurationDialog/ProductPricing";
import { ProductTagsSelector } from "../../SalesProductsPage/ProductConfigurationDialog/ProductTagsSelector";
import { RelatedProductsControl } from "../../SalesProductsPage/ProductConfigurationDialog/RelatedProductsControl";
import { ProductPassportAccordionHeader } from "./ProductPassportAccordionHeader";
import { ProductPassportAgreementsTable } from "./ProductPassportAgreementsTable";
import { ProductPassportBookingsTable } from "./ProductPassportBookingsTable";
import { ProductPassportOffersTable } from "./ProductPassportOffersTable";
import IconButtonWithTooltip from "../../../components/button/IconButtonWithTooltip";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import {Grid} from "@mui/material";

export const ProductPassport = ({ product }) => {
    const { t } = useTranslation();
    const navigate = useNavigate()

    const handleBlockProduct = () => {
        console.log("Block product clicked for:", product.id, product.name_full);
    };

    const initialFormValues = {
        name: product.name_full,
        type: product.type?.id ?? "",
        productNumber: product.product_number,
        floor: product.floor,
        num_rooms: product.num_rooms,
        status: product.status.id,
        style: product.style?.id,
        sideEast: product.orientation.east,
        sideWest: product.orientation.west,
        sideNorth: product.orientation.north,
        sideSouth: product.orientation.south,
        footage: product.footage ? product.footage : 0,
        footageFinal: product.footage_postconstruction
            ? product.footage_postconstruction
            : undefined,
        kind: product.kind?.id ?? "",
        pricing: {
            vat:
                typeof product?.pricing.vat_proc === "string"
                    ? Number.parseFloat(product.pricing.vat_proc || 0)
                    : product.pricing.vat_proc,
            maxDiscount:
                typeof product.pricing.discount_max === "string"
                    ? Number.parseFloat(product.pricing.discount_max)
                    : product.pricing.discount_max,
            listedNetPerSqm: Number.parseFloat(product.pricing.listed_net_per_m2),
            minNetPerSqm: Number.parseFloat(product.pricing.min_net_per_m2),
            baseNetPerSqm: Number.parseFloat(product.pricing.base_net_per_m2),
        },
        tags: product.tags,
        note: product.note,
        hotel_note: product.hotel_note,
        childrenIds: product.children?.map((child) => child.id) ?? [],
    };

    const productParametersFieldNames = {
        name: "name",
        type: "type",
        productNumber: "productNumber",
        floor: "floor",
        num_rooms: "num_rooms",
        status: "status",
        style: "style",
        sides: {
            east: "sideEast",
            west: "sideWest",
            north: "sideNorth",
            south: "sideSouth",
        },
        footage: "footage",
        footageFinal: "footageFinal",
    };

    const productPricingFieldNames = {
        kind: "kind",
        footage: "footage",
        footageFinal: "footageFinal",
        pricing: {
            maxDiscount: "pricing.maxDiscount",
            vat: "pricing.vat",
            listedNetPerSqm: "pricing.listedNetPerSqm",
            minNetPerSqm: "pricing.minNetPerSqm",
            baseNetPerSqm: "pricing.baseNetPerSqm",
        },
    };

    const methods = useForm({
        defaultValues: initialFormValues,
    });

    const { control, setValue, watch } = methods;

    const formTags = watch("tags");
    const setFormTagIds = (ids) => {
        const updatedTags = ids.map((id) => {
            const existingTag = formTags.find((tag) => tag.id === id);
            return existingTag || { id, tag_name: "" };
        });
        setValue("tags", updatedTags);
    };

    return (
        <FormProvider {...methods}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    padding: 3,
                    minHeight: "100vh",
                    backgroundColor: "#f5f5f5",
                    maxWidth: "90vw",
                    margin: "0 auto",
                }}
            >

                <Grid
                    container
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    sx={{ p: 2 }}
                >   
                    <Grid item xs={0.1}>
                        <IconButtonWithTooltip
                            title={t("back")}
                            onClick={()=>navigate("/products/")}
                        >
                            <ArrowBackIcon sx={{ color: "var(--primary)" }} fontSize="large" />
                        </IconButtonWithTooltip>
                    </Grid>
                    <Grid item xs={11.8}>
                    <Typography variant="h2" sx={{ fontSize: "2rem", fontWeight: "bold", mb: 2 }}>
                            {t("products.ProductPassport.title")}: {product.name_full}
                        </Typography>
                    </Grid>
                    <Grid item xs={0.1}></Grid>
                </Grid>
                <ProductParametersWithBlockButton
                    control={control}
                    investmnetHomeId={product.investment?.id_invest_home}
                    fieldNames={productParametersFieldNames}
                    onBlockProduct={handleBlockProduct}
                    productId={product.id}
                    productStatusId={product?.status?.id}
                />

                <Paper
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                        padding: "2rem",
                    }}
                >
                    <Typography
                        variant="h3"
                        align="left"
                        sx={{ width: "100%", fontSize: "1.5rem" }}
                    >
                        {t("products.ProductPassport.facilities")}
                    </Typography>
                    <Box display="flex" flexDirection="row" gap={3} width="100%">
                        <ProductTagsSelector
                            formTagIds={formTags.map((tag) => tag.id)}
                            setFormTagIds={setFormTagIds}
                            readOnly={true}
                        />
                        <RelatedProductsControl
                            readOnly={true}
                            relatedProductIds={watch("childrenIds")}
                            // No-op since readOnly
                            setRelatedProductIds={() => {}}
                        />
                    </Box>
                </Paper>

                <ProductPricing
                    control={control}
                    fieldNames={productPricingFieldNames}
                    readOnly={true}
                    passportView={true}
                    productId={product.id}
                />

                <Box display="flex" flexDirection="row" gap={3} width="100%">
                    <Box sx={{ flex: 1 }}>
                        <ProductNoteControl control={control} fieldName="note" readOnly={true}/>
                        <ProductHotelNoteControl control={control} fieldName="hotel_note" readOnly={true}/>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <ProductDocuments product={product} />
                    </Box>
                </Box>
                <Box display="flex" flexDirection="row" gap={3} width="100%">
                    <Box sx={{ flex: 2 }}>
                        <ProductCards product={product} />
                    </Box>
                </Box>
                <Box sx={{ width: "100%" }}>
                    <Accordion defaultExpanded elevation={0} sx={{ bgcolor: "whitesmoke" }}>
                        <ProductPassportAccordionHeader
                            title={t("products.ProductPassport.offers")}
                        />
                        <AccordionDetails>
                            <ProductPassportOffersTable productId={product.id} />
                        </AccordionDetails>
                    </Accordion>
                </Box>

                <Box sx={{ width: "100%" }}>
                    <Accordion defaultExpanded elevation={0} sx={{ bgcolor: "whitesmoke" }}>
                        <ProductPassportAccordionHeader
                            title={t("products.ProductPassport.bookings")}
                        />
                        <AccordionDetails>
                            <ProductPassportBookingsTable productId={product.id} />
                        </AccordionDetails>
                    </Accordion>
                </Box>

                <Box sx={{ width: "100%" }}>
                    <Accordion defaultExpanded elevation={0} sx={{ bgcolor: "whitesmoke" }}>
                        <ProductPassportAccordionHeader
                            title={t("products.ProductPassport.agreements")}
                        />
                        <AccordionDetails>
                            <ProductPassportAgreementsTable productId={product.id} />
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </Box>
        </FormProvider>
    );
};

const ProductParametersWithBlockButton = (props) => {
    const { t } = useTranslation();

    return (
        <Paper
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
                padding: "2rem",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                }}
            >
                <Typography variant="h3" align="left" sx={{ fontSize: "1.5rem" }}>
                    {t("products.ProductParameters.title")}
                </Typography>
                {/* <Button
                    variant="contained"
                    color="error"
                    onClick={props.onBlockProduct}
                    sx={{ textTransform: "none", minWidth: "120px" }}
                    // FIXME: Enable when functionality is implemented
                    disabled={true}
                >
                    {t("products.ProductPassport.blockButton")}
                </Button> */}
            </Box>
            <Box sx={{ width: "100%" }}>
                <ProductParameters
                    control={props.control}
                    forMultipleProducts={false}
                    fieldNames={props.fieldNames}
                    readOnly={true}
                    hideTitle={true}
                    investmnetHomeId={props.investmnetHomeId}
                    productId={props.productId}
                    productStatusId={props.productStatusId}
                    
                />
            </Box>
        </Paper>
    );
};

const ProductNoteControlDisabled = (props) => {
    const { control, fieldName } = props;
    const { t } = useTranslation();

    return (
        <Paper
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                padding: "2rem",
            }}
        >
            <Typography variant="h3" sx={{ fontSize: "1.5rem" }}>
                {t("products.ProductConfigurationDialog.productNote")}
            </Typography>
            <ProductNoteControl control={control} fieldName={fieldName} readOnly={true} />
        </Paper>
    );
};
